import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import * as api from "../service/heroService";
import type { HeroSection, HeroWord } from "../types";

interface HeroState {
  section: HeroSection | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: HeroState = { section: null, loading: false, saving: false, error: null };

export const fetchHero = createAsyncThunk("cms/hero/fetch", api.getHero);
export const saveHeroSection = createAsyncThunk(
  "cms/hero/save",
  (payload: Pick<HeroSection, "headingLine1" | "headingLine2" | "paragraph">) => api.updateHero(payload)
);
export const addWord = createAsyncThunk("cms/hero/addWord", api.addHeroWord);
export const removeWord = createAsyncThunk("cms/hero/removeWord", api.removeHeroWord);

// Moves a word up/down by swapping displayOrder with its neighbour. Both
// words' new positions are persisted so the order survives a refetch.
export const moveWord = createAsyncThunk(
  "cms/hero/moveWord",
  async (payload: { wordId: number; direction: "up" | "down" }, { getState }) => {
    const state = getState() as { cmsHero: HeroState };
    const words = state.cmsHero.section?.rotatingWords ?? [];
    const sorted = [...words].sort((a, b) => a.displayOrder - b.displayOrder);
    const index = sorted.findIndex((w) => w.id === payload.wordId);
    const swapIndex = payload.direction === "up" ? index - 1 : index + 1;
    if (index === -1 || swapIndex < 0 || swapIndex >= sorted.length) {
      return { moved: sorted[index], swapped: undefined as HeroWord | undefined };
    }

    const moved = sorted[index];
    const swapped = sorted[swapIndex];
    const [movedOrder, swappedOrder] = [swapped.displayOrder, moved.displayOrder];

    await Promise.all([
      api.reorderHeroWord({ wordId: moved.id, displayOrder: movedOrder }),
      api.reorderHeroWord({ wordId: swapped.id, displayOrder: swappedOrder }),
    ]);

    return {
      moved: { ...moved, displayOrder: movedOrder },
      swapped: { ...swapped, displayOrder: swappedOrder },
    };
  }
);

const heroSlice = createSlice({
  name: "cmsHero",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHero.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchHero.fulfilled, (state, action: PayloadAction<HeroSection>) => {
        state.loading = false;
        state.section = action.payload;
      })
      .addCase(fetchHero.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load hero section.";
      })
      .addCase(saveHeroSection.pending, (state) => { state.saving = true; })
      .addCase(saveHeroSection.fulfilled, (state, action: PayloadAction<HeroSection>) => {
        state.saving = false;
        if (state.section) {
          state.section.headingLine1 = action.payload.headingLine1;
          state.section.headingLine2 = action.payload.headingLine2;
          state.section.paragraph = action.payload.paragraph;
        } else {
          state.section = action.payload;
        }
      })
      .addCase(saveHeroSection.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? "Failed to save hero section.";
      })
      .addCase(addWord.fulfilled, (state, action: PayloadAction<HeroWord>) => {
        state.section?.rotatingWords.push(action.payload);
      })
      .addCase(removeWord.fulfilled, (state, action: PayloadAction<number>) => {
        if (!state.section) return;
        state.section.rotatingWords = state.section.rotatingWords.filter((w) => w.id !== action.payload);
      })
      .addCase(moveWord.fulfilled, (state, action) => {
        const { moved, swapped } = action.payload;
        if (!state.section || !moved || !swapped) return;
        state.section.rotatingWords = state.section.rotatingWords.map((w) => {
          if (w.id === moved.id) return moved;
          if (w.id === swapped.id) return swapped;
          return w;
        });
      });
  },
});

export default heroSlice.reducer;